<script setup lang="ts">
import type { Project } from '#auth/composables/useAuth'
import { useAdvisor } from '#advisor-portal/state/useAdvisor'
import { projectLandingPath } from '#core/utils/project-paths'
import { MemberStatusEnum } from '@forgd/shared/enums'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { Button } from '@forgd/ui-kit/button'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@forgd/ui-kit/empty'
import { Separator } from '@forgd/ui-kit/separator'
import {
  ArrowRightStartOnRectangleIcon,
  CheckIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  CubeIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import { useMutation } from '@pinia/colada'

const emit = defineEmits<{ close: [] }>()

const auth = useAuth()
const advisor = useAdvisor()
const router = useRouter()
const toast = useAppToast()

const { project, organizations } = storeToRefs(auth)
const { project: advisorProject, isCreateEngagementOpen } = storeToRefs(advisor)

const activeOrg = computed(() =>
  (organizations.value || []).find(o => o.projects.some(p => p.id === project.value?.id)),
)
const activeProjectEntry = computed(() => activeOrg.value?.projects.find(p => p.id === project.value?.id))
const needsTokenBasics = computed(() => !!activeProjectEntry.value && !activeProjectEntry.value.isOnboarded)
const activeProjectRole = computed(() => {
  if (auth.isAdvisor) {
    return 'Advisor'
  }
  return activeOrg.value?.ownerUserId === auth.me?.id ? 'Admin' : 'Teammate'
})

const hasProjects = computed(() => {
  if (!organizations.value) {
    return false
  }
  return organizations.value.filter(org => org.memberStatus === MemberStatusEnum.Active).filter(org => org.projects.length).length > 1
})

// Orgs you've been invited to but haven't joined yet — surfaced as pending
// invitations. Accepting routes into the teammate onboarding flow for that org.
const invitedOrgs = computed(() =>
  (organizations.value || []).filter(
    org => org.memberStatus === MemberStatusEnum.Invited && org.ownerUserId !== auth.me?.id,
  ),
)

function isProjectSelected(id: string) {
  if (auth.isAdvisor) {
    return !advisor.isInPortal && advisorProject.value?.id === id
  }

  return project.value?.id === id
}

async function switchProject(newProject: Project) {
  emit('close')

  const currentProjectId = auth.project?.id
  auth.setSelectedProject(newProject)
  // if the current path includes the current project id, keep the same path but replace the project id
  if (currentProjectId && router.currentRoute.value.path.includes(currentProjectId)) {
    const newPath = router.currentRoute.value.path.replace(currentProjectId, newProject.id)
    await navigateTo(newPath, { external: true })
    return
  }

  // otherwise, send the user to the new project home
  await navigateTo(projectLandingPath(newProject.id), { external: true })
}

function goToAdvisorPortal() {
  emit('close')
  return navigateTo('/advisor-portal')
}

function acceptInvitation(organizationId: string) {
  emit('close')
  return navigateTo(`/onboarding/${organizationId}/teammate`)
}

const { mutateAsync: rejectInvitationAsync } = useMutation(
  buildApiMutation('/membership/reject', { method: 'post' }),
)
const rejectingOrganizationId = ref<string | null>(null)

async function rejectInvitation(organizationId: string) {
  rejectingOrganizationId.value = organizationId

  try {
    await rejectInvitationAsync({ body: { organizationId } })
    await auth.refresh({ from: 'CoreHeaderProfileContent' })
    toast.success({ title: 'Invitation rejected' })
  }
  catch (error) {
    const apiError = error as { message?: string }
    toast.error({ title: 'Failed to reject the invitation', description: apiError?.message })
  }

  rejectingOrganizationId.value = null
}
</script>

<template>
  <div class="w-full">
    <div class="p-4">
      <div class="flex flex-col leading-normal">
        <span class="text-xs text-secondary">
          Signed in as
        </span>
        <span v-if="auth.me" class="text-sm font-semibold text-primal">
          {{ auth.me.firstName }} {{ auth.me.lastName }}
        </span>
        <span v-if="auth.me" class="text-xs text-secondary">
          {{ auth.me.email }}
        </span>
      </div>
      <div class="mt-4 flex w-full items-start justify-between">
        <Button as-child variant="outline" size="xs">
          <NuxtLink data-testid="account-settings" :to="getProjectPath('/settings')" @click="emit('close')">
            Account Settings
            <Cog6ToothIcon class="size-3" />
          </NuxtLink>
        </Button>
        <Button data-testid="logout" variant="outline" size="xs" class="w-20" @click="auth.logout()">
          Logout
          <ArrowRightStartOnRectangleIcon class="size-3" />
        </Button>
      </div>
    </div>
    <template v-if="project && (!auth.isAdvisor || !advisor.isInPortal)">
      <Separator />
      <div class="flex flex-col gap-4 p-4">
        <div class="flex w-full items-center gap-2">
          <ProjectAvatar :src="project.image" size="sm" />
          <div class="flex min-w-0 flex-1 items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-2">
              <span class="truncate text-sm font-semibold text-primal uppercase">{{ project.ticker || project.name }}</span>
              <span class="inline-flex h-6 shrink-0 items-center rounded-full bg-secondary/10 px-2 font-mono text-xs font-medium tracking-[-0.2px] text-secondary">
                {{ project.tokenListed ? 'Listed' : 'Pre-TGE' }}
              </span>
            </div>
            <span class="shrink-0 text-xs text-secondary">{{ activeProjectRole }}</span>
          </div>
        </div>

        <div v-if="!auth.isAdvisor && needsTokenBasics" class="flex items-start gap-2 rounded-lg bg-neutral-600/30 p-2">
          <InformationCircleIcon class="size-6 shrink-0 text-secondary" />
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <div class="flex flex-col text-xs text-secondary">
              <span class="font-semibold">Configure your token basics</span>
              <span>Add pending details of your token to improve your experience.</span>
            </div>
            <Button as-child variant="outline" size="xs" class="w-fit">
              <NuxtLink :to="getProjectPath('/token-settings')" @click="emit('close')">
                Edit Token Settings
              </NuxtLink>
            </Button>
          </div>
        </div>
        <Button v-else as-child variant="outline" size="xs" class="w-fit">
          <NuxtLink :to="getProjectPath('/token-settings')" @click="emit('close')">
            Token settings
          </NuxtLink>
        </Button>
      </div>
    </template>
    <template v-if="auth.isAdvisor">
      <Separator />
      <div class="my-2 p-2">
        <button type="button" class="flex w-full items-center justify-between rounded-xl px-2 py-1 hover:bg-neutral-200" @click="goToAdvisorPortal">
          <div class="flex gap-2">
            <ProjectAvatar :src="advisor.organization?.image" size="md" />
            <div class="flex flex-col text-left">
              <div class="max-w-37.5 truncate font-semibold uppercase">
                {{ advisor.organization?.name }}
              </div>
              <div class="text-xs text-accent">
                Your Organization Dashboard
              </div>
            </div>
          </div>
          <div v-if="advisor.isInPortal" class="flex size-8 items-center justify-center rounded-full border border-green-600/30 bg-green-600/10">
            <CheckIcon class="size-4 text-green-600" />
          </div>
          <ChevronRightIcon v-else class="size-4" />
        </button>
      </div>
    </template>
    <Separator v-if="hasProjects" />
    <div v-if="hasProjects" class="py-4">
      <div class="mb-6 px-4 text-xs text-secondary">
        {{ auth.isAdvisor ? 'Switch between assigned projects' : 'Switch between active tokens in Forgd' }}
      </div>
      <div class="space-y-1 px-2 md:max-h-87.5 md:overflow-y-auto">
        <template v-for="org in (organizations || []).filter(org => org.memberStatus === MemberStatusEnum.Active)">
          <button
            v-for="p in org.projects.filter(proj => !isProjectSelected(proj.id))"
            :key="p.id"
            type="button"
            class="flex w-full items-center justify-between rounded-xl px-2 py-1 hover:bg-neutral-200"
            @click="switchProject(p)"
          >
            <div class="flex items-center gap-2 text-left">
              <ProjectAvatar :src="p.image" size="md" />
              <div class="flex flex-col">
                <div class="max-w-37.5 truncate font-semibold uppercase">
                  {{ p.ticker }}
                </div>
                <div class="max-w-37.5 truncate text-xs text-secondary">
                  {{ p.name }}
                </div>
                <div class="text-xs text-accent">
                  <template v-if="p.isResearch">
                    Researcher account
                  </template>
                  <template v-else-if="auth.isAdvisor">
                    Advisor
                  </template>
                  <template v-else>
                    {{ org.ownerUserId === auth.me?.id ? 'Token Admin' : 'Teammate' }}
                  </template>
                </div>
              </div>
            </div>
            <ChevronRightIcon class="size-4" />
          </button>
        </template>
      </div>
    </div>
    <div v-else-if="auth.isAdvisor" class="py-4">
      <Empty class="min-h-45">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CubeIcon aria-hidden="true" />
          </EmptyMedia>
          <EmptyTitle class="text-sm">
            Assigned projects will appear here
          </EmptyTitle>
          <EmptyDescription class="mx-auto w-3/4 text-xs">
            This will be your access point to navigate between them seamlessly.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button size="sm" @click="isCreateEngagementOpen = true">
            Add New Engagement
            <PlusIcon />
          </Button>
        </EmptyContent>
      </Empty>
    </div>
    <template v-if="invitedOrgs.length">
      <Separator />
      <div class="flex flex-col gap-2 p-2">
        <div class="flex flex-col p-2 leading-normal">
          <span class="text-sm font-semibold text-primal">Join other token spaces</span>
          <span class="text-xs text-secondary">Approve or reject pending invitations.</span>
        </div>
        <div
          v-for="org in invitedOrgs"
          :key="org.id"
          class="flex items-center justify-between gap-2 rounded-sm p-2"
        >
          <div class="flex min-w-0 items-center gap-3">
            <ProjectAvatar :src="org.image" size="md" />
            <div class="flex min-w-0 flex-col">
              <span class="truncate text-sm font-semibold text-primal">{{ org.projects?.[0]?.ticker || org.name }}</span>
              <span v-if="org.projects?.[0]?.name" class="truncate text-xs text-secondary">{{ org.projects[0].name }}</span>
              <span class="text-xs text-accent">Invited as teammate</span>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              icon-only
              aria-label="Accept invitation"
              :disabled="rejectingOrganizationId === org.id"
              @click="acceptInvitation(org.id)"
            >
              <CheckIcon />
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon-only
              aria-label="Reject invitation"
              :loading="rejectingOrganizationId === org.id"
              @click="rejectInvitation(org.id)"
            >
              <TrashIcon />
            </Button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
